import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl, FormattedMessage } from 'react-intl'
import { IconButton } from '../layout/Buttons'
import { getAggregateParam } from '../../selectors/collectionSelectors'
import { setParam } from '../../actions/collectionActions'
import {
  getStoredAggregateState,
  saveAggregateState,
  getAggregateButtonColor
} from '../../utilities/gridAggregateUtility'

/**
 * AggregationToggle Component
 *
 * Toggle button to switch between showing only approved versions (aggregate: true)
 * and all versions (aggregate: false) of entities.
 *
 * State is stored in both:
 * - Redux params (for current session state)
 * - localStorage (for persistence across navigation)
 *
 * @param {Object} props
 * @param {string} props.collection - Collection name (e.g., 'employees')
 * @param {Function} props.onClick - Optional callback when toggle is clicked
 */
const AggregationToggle = (props) => {
  const { onClick, collection } = props
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  // Get current aggregate state from Redux params
  const aggregateParam = useSelector((state) => getAggregateParam(state, collection))

  // Derive showAllVersions from aggregate param (aggregate: true = showAllVersions: false)
  const showAllVersions = aggregateParam === false

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedShowAllVersions = getStoredAggregateState(collection)
    const storedAggregate = !storedShowAllVersions

    // Only set if different from current Redux state
    if (aggregateParam !== storedAggregate) {
      dispatch(setParam(collection, 'aggregate', storedAggregate))
    }
  }, [collection, dispatch, aggregateParam])

  const handleToggle = () => {
    const newShowAllVersions = !showAllVersions
    const newAggregate = !newShowAllVersions

    // Update Redux param
    dispatch(setParam(collection, 'aggregate', newAggregate))

    // Persist to localStorage
    saveAggregateState(collection, newShowAllVersions)

    // Call optional callback
    if (onClick) {
      onClick(newAggregate)
    }
  }

  return (
    <IconButton
      iconName="Org"
      color={getAggregateButtonColor(showAllVersions)}
      title={formatMessage({ id: 'button.aggregate_on' })}
      onClick={handleToggle}
    >
      <FormattedMessage id="button.versions_all" />
    </IconButton>
  )
}

export default AggregationToggle
